import {KpiAssignment, KpiCalculationTimeFrameDto} from '@hconnect/common/types'
import {formatNumber} from '@hconnect/common/utils'
import {KpiScore, customThemeConstants} from '@hconnect/uikit/src/lib2'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {Box, Grid, useMediaQuery, Theme, Typography, styled} from '@mui/material'
import {useTranslation} from 'react-i18next'

import {KPIS_WIDGET_NAMESPACE} from '../consts'

import {KpiThresholdIcon} from './KpiThresholdIcon'

type Props = {
  kpiAssignment: KpiAssignment
  timeFrames: KpiCalculationTimeFrameDto[]
}

const StyledBoxNoData = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5)
}))

export const EquipmentKpiAssignment = ({kpiAssignment, timeFrames}: Props) => {
  const {
    t,
    i18n: {language}
  } = useTranslation(KPIS_WIDGET_NAMESPACE)

  const displayTwoItemsInRow = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))

  return (
    <>
      <Box>
        <Typography
          variant="h5"
          sx={{
            mt: 1.5,
            mb: 1.5
          }}
        >
          {kpiAssignment.kpiName}
        </Typography>
      </Box>
      <Grid
        container
        spacing={1.5}
        sx={{
          borderBottom: `1px solid ${customThemeConstants().palette.neutralFaded20}`,
          pb: 1
        }}
      >
        {timeFrames.map((timeframe) => {
          const kpiResult = kpiAssignment.timeFrameResults![timeframe.calculationTimeFrameType]

          if (kpiResult) {
            const isValid = !isNaN(parseFloat(kpiResult.value))
            const kpiValue = isValid ? formatNumber(parseFloat(kpiResult.value), language) : '-'
            const kpiUnit = isValid ? kpiResult.unit : ''
            return (
              <Grid
                item
                xs={displayTwoItemsInRow ? 6 : 4}
                key={`grid-${kpiAssignment.kpiName}-${timeframe.calculationTimeFrameType}`}
              >
                <KpiScore
                  variant={'neutral'}
                  value={kpiValue}
                  label={t(`equipmentKpi.timeFrameType.${timeframe.calculationTimeFrameType}`)}
                  unit={kpiUnit}
                  valueIcon={
                    kpiResult?.kpiBreachSeverity ? (
                      <KpiThresholdIcon status={kpiResult.kpiBreachSeverity} variant="filled" />
                    ) : undefined
                  }
                  size={'S'}
                  data-test-id={`kpi-score-${kpiAssignment.kpiName}-${timeframe.calculationTimeFrameType}`}
                />
              </Grid>
            )
          }

          return (
            <Grid
              item
              xs={displayTwoItemsInRow ? 6 : 4}
              key={`grid-${kpiAssignment.kpiName}-${timeframe.calculationTimeFrameType}`}
            >
              <Typography variant="caption">
                {t(`equipmentKpi.timeFrameType.${timeframe.calculationTimeFrameType}`)}
              </Typography>
              <StyledBoxNoData>
                <InfoOutlinedIcon sx={{m: 1}} />
                <Typography variant="body1">{t('equipmentKpi.message.noData')}</Typography>
              </StyledBoxNoData>
            </Grid>
          )
        })}
      </Grid>
    </>
  )
}
